'use client';

import { c as _c } from "react/compiler-runtime";
import { getFamilyLinks, useGnbAuth } from '@/features/gnb';
import { useLanguage } from '@/features/language';
import { type Theme, useTheme } from '@/features/theme';
import { ENV } from '@/shared/config';
import { getHostOrigin } from '@/shared/lib/path';
import { DEFAULT_LANGUAGE, isSupportedLocale } from '@/shared/localization';
import { login } from '@/shared/utils';
import { Gnb } from '@ncai-axe/react-ui-v3';
import { getCommerceProductInfo, getIntroMenuLinks } from './consts';
type Props = {
  locale: string;
  /**
   * SSR 에서 세션 쿠키로 판별된 초기 인증 상태.
   * 부모 server layout 에서 `cookies()` 로 읽어 넘긴다.
   * zustand store hydrate 이전, 첫 SSR HTML 시점에 헤더가 "로그인" 버튼을 잘못 그리지 않도록 사용.
   */
  initialIsAuthenticated: boolean;
  /**
   * SSR 에서 theme 쿠키로 확정한 초기 테마.
   * 부모 server layout 에서 `cookies()` 로 읽어 넘긴다.
   * 헤더는 client 컴포넌트라 server 에서 document 를 못 읽어 DEFAULT_THEME 으로 떨어지는데,
   * 그러면 root `<html data-theme>` 와 헤더 `data-theme` 가 어긋나 배경이 테마를 안 따라간다.
   */
  initialTheme: Theme;
};

// 세션 쿠키는 있는데 /user 응답이 아직 안 온 hydration gap 동안 사용할 placeholder.
// "로그인" 버튼이 한 프레임이라도 깜빡이지 않도록 UserSection 분기로 진입시키는 용도.
// 빈 문자열로 두어 이름/이메일/이니셜이 비어 보이지만, 곧바로 실제 정보로 교체된다.
const PLACEHOLDER_USER = {
  name: '',
  email: ''
} as const;
const IntroPackageHeader = t0 => {
  const $ = _c(23);
  const {
    locale,
    initialIsAuthenticated,
    initialTheme
  } = t0;
  const {
    gnbUser,
    isAuthenticated,
    handleLogout
  } = useGnbAuth();
  const [currentLocale, setLanguage] = useLanguage();
  const [theme] = useTheme(initialTheme);
  const lang = (currentLocale ?? locale) as 'ko' | 'en';
  let t1;
  if ($[0] !== lang) {
    t1 = isSupportedLocale(lang) ? lang : DEFAULT_LANGUAGE;
    $[0] = lang;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const routeLocale = t1;
  const looksAuthenticated = isAuthenticated || initialIsAuthenticated;
  const headerUser = gnbUser ?? (looksAuthenticated ? PLACEHOLDER_USER : null);
  let t2;
  if ($[2] !== routeLocale) {
    t2 = () => {
      login(ENV.AUTH_API_BASE_URL, `${getHostOrigin()}/${routeLocale}/products`);
    };
    $[2] = routeLocale;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleLogin = t2;
  let t3;
  if ($[4] !== routeLocale) {
    t3 = getCommerceProductInfo(routeLocale);
    $[4] = routeLocale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== theme) {
    t4 = {
      value: theme,
      enabled: false
    };
    $[6] = theme;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== routeLocale) {
    t5 = getIntroMenuLinks(routeLocale);
    $[8] = routeLocale;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== lang) {
    t7 = getFamilyLinks(lang);
    $[11] = lang;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== handleLogin || $[14] !== handleLogout || $[15] !== headerUser || $[16] !== lang || $[17] !== setLanguage || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t7) {
    t8 = <Gnb packageMode={true} centerDesktopMenu={true} brandLogo="vaetki" product={t3} language={lang} theme={t4} menuLinks={t5} user={headerUser} userLinks={t6} familyLinks={t7} onChangeLanguage={setLanguage} onClickLogin={handleLogin} onClickLogout={handleLogout} />;
    $[13] = handleLogin;
    $[14] = handleLogout;
    $[15] = headerUser;
    $[16] = lang;
    $[17] = setLanguage;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
export default IntroPackageHeader;